/**
 * GUEST ORDERING SETTINGS — per-restaurant toggles for the guest table page
 * and service requests. Later pieces add pay-at-table and tabs onto this same
 * object (`payAtTable`, `payFirst`, `tabs`, `fireMode`, `tabHoldCents`,
 * `tabAutoCloseMinutes` already exist here so those pieces have no migration
 * to do).
 *
 * `resolveGuestOrderingSettings` is the single place a default is decided —
 * every reader (the guest table page, service-request creation, and later
 * pay-at-table/tabs code) calls it against `restaurant.settings?.guestOrdering`
 * rather than reading the raw optional object, so an `undefined` field never
 * leaks into a caller's boolean check as `undefined` instead of a chosen
 * `true`/`false`.
 *
 * Per spec §7: a restaurant that has not chosen never takes a guest card —
 * `payFirst` and `tabs` default to `false`. `tablePage`, `serviceRequests` and
 * `payAtTable` default to `true`: they are the baseline guest experience this
 * platform ships, and `fireMode` defaults to `"immediate"` (no confirmation
 * step) until a restaurant opts into one of the confirm modes.
 */

export const GUEST_FIRE_MODES = ["immediate", "confirmAlcohol", "confirmTabRounds", "confirmAll"] as const;
export type GuestFireMode = (typeof GUEST_FIRE_MODES)[number];

export interface IGuestOrderingSettings {
  readonly tablePage?: boolean;
  readonly serviceRequests?: boolean;
  readonly payAtTable?: boolean;
  readonly payFirst?: boolean;
  readonly tabs?: boolean;
  readonly fireMode?: GuestFireMode;
  readonly tabHoldCents?: number;
  readonly tabAutoCloseMinutes?: number;
}

/** Every field resolved — never partial, never `undefined`. */
export interface ResolvedGuestOrderingSettings {
  readonly tablePage: boolean;
  readonly serviceRequests: boolean;
  readonly payAtTable: boolean;
  readonly payFirst: boolean;
  readonly tabs: boolean;
  readonly fireMode: GuestFireMode;
  readonly tabHoldCents: number;
  readonly tabAutoCloseMinutes: number;
}

export const GUEST_ORDERING_DEFAULTS: ResolvedGuestOrderingSettings = {
  tablePage: true,
  serviceRequests: true,
  payAtTable: true,
  payFirst: false,
  tabs: false,
  fireMode: "immediate",
  tabHoldCents: 5000,
  tabAutoCloseMinutes: 180,
};

/**
 * Resolves a possibly-partial, possibly-absent guest ordering settings object
 * against {@link GUEST_ORDERING_DEFAULTS}. `null` and `undefined` both resolve
 * to the defaults outright — a restaurant document written before this field
 * existed carries neither.
 */
export function resolveGuestOrderingSettings(
  value: IGuestOrderingSettings | null | undefined,
): ResolvedGuestOrderingSettings {
  return {
    tablePage: value?.tablePage ?? GUEST_ORDERING_DEFAULTS.tablePage,
    serviceRequests: value?.serviceRequests ?? GUEST_ORDERING_DEFAULTS.serviceRequests,
    payAtTable: value?.payAtTable ?? GUEST_ORDERING_DEFAULTS.payAtTable,
    payFirst: value?.payFirst ?? GUEST_ORDERING_DEFAULTS.payFirst,
    tabs: value?.tabs ?? GUEST_ORDERING_DEFAULTS.tabs,
    fireMode: value?.fireMode ?? GUEST_ORDERING_DEFAULTS.fireMode,
    tabHoldCents: value?.tabHoldCents ?? GUEST_ORDERING_DEFAULTS.tabHoldCents,
    tabAutoCloseMinutes: value?.tabAutoCloseMinutes ?? GUEST_ORDERING_DEFAULTS.tabAutoCloseMinutes,
  };
}
